import {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Plus, Search } from "lucide-react";

import api from "../../../lib/api";
import { exportCrmExcel } from "../../../lib/crmExcel";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { Pagination } from "../../../components/ui/Pagination";
import {
  Table,
  type TableColumn,
} from "../../../components/ui/Table";
import { CrmEmpresaFichaModal } from "./CrmEmpresaFichaModal";
import { CrmOrigenSelect } from "./CrmOrigenSelect";
import { useAuth } from "../../../context/AuthContext";

interface CrmCliente {
  id: number;
  idExterno: string | null;
  razonSocial: string;
  nombreComercial: string | null;
  cif: string | null;
  direccion: string | null;
  poblacion: string | null;
  codigoPostal: string | null;
  pais: string;
  personaContacto: string | null;
  telefono: string | null;
  emailContacto: string | null;
  estado: "activo" | "inactivo" | "archivado";
  estadoComercial: "prospect" | "cliente" | "inactivo";
  origen: string | null;
}

interface ClientesResponse {
  data: CrmCliente[];
  total: number;
  page: number;
  totalPages: number;
}

type TipoPoliza =
  | "ACC"
  | "PTD"
  | "KARTING_ALQUILER"
  | "RC_CIRCUITO"
  | "RC_ORGANIZADOR"
  | "RC_EMPRESA"
  | "MULTIRRIESGO_EMPRESA"
  | "RC_PENDIENTE";

interface ClienteForm {
  razonSocial: string;
  cif: string;
  direccion: string;
  poblacion: string;
  codigoPostal: string;
  pais: string;
  personaContacto: string;
  telefono: string;
  emailContacto: string;

  tipoPoliza: TipoPoliza;
  numeroPoliza: string;
  aseguradora: string;
  fechaEfecto: string;
  fechaVencimiento: string;

  primaNeta: string;
impuestos: string;
recargo: string;
consorcio: string;
primaTotal: string;
comisionPorcentaje: string;
  emailAcceso: string;
  passwordAcceso: string;
  metodoPago: string;
}

interface ProspectForm {
  razonSocial: string;
  nombreComercial: string;
  cif: string;
  personaContacto: string;
  telefono: string;
  emailContacto: string;
  origen: string;
}

const EMPTY_FORM: ClienteForm = {
  razonSocial: "",
  cif: "",
  direccion: "",
  poblacion: "",
  codigoPostal: "",
  pais: "España",
  personaContacto: "",
  telefono: "",
  emailContacto: "",

  tipoPoliza: "ACC",
  numeroPoliza: "",
  aseguradora: "Lloyd’s Insurance Company S.A.",
  fechaEfecto: "",
  fechaVencimiento: "",

  primaNeta: "",
impuestos: "",
recargo: "",
consorcio: "",
primaTotal: "",
comisionPorcentaje: "",
  emailAcceso: "",
  passwordAcceso: "",
  metodoPago: "tpv",
};

const EMPTY_PROSPECT_FORM: ProspectForm = {
  razonSocial: "",
  nombreComercial: "",
  cif: "",
  personaContacto: "",
  telefono: "",
  emailContacto: "",
  origen: "",
};

const TIPOS_POLIZA: Array<{
  value: TipoPoliza;
  label: string;
}> = [
  {
    value: "ACC",
    label: "Accidentes circuito",
  },
  {
    value: "PTD",
    label: "Invalidez permanente (PTD)",
  },
  {
    value: "KARTING_ALQUILER",
    label: "Accidentes flotas de kart",
  },
  {
    value: "RC_CIRCUITO",
    label: "RC para circuitos",
  },
  {
    value: "RC_ORGANIZADOR",
    label: "RC para organizadores",
  },
  {
    value: "RC_EMPRESA",
    label: "RC para empresas",
  },
  {
    value: "MULTIRRIESGO_EMPRESA",
    label: "Multirriesgo empresas",
  },
];

function formatDateInput(value: string): string {
  const digits = value
    .replace(/\D/g, "")
    .slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(
    2,
    4
  )}/${digits.slice(4)}`;
}

function parseSpanishDate(
  value: string
): string | null {
  const match =
    /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(
    Date.UTC(year, month - 1, day)
  );

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${String(year).padStart(
    4,
    "0"
  )}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}

function calculateAnnualExpiry(
  value: string
): string {
  const isoDate = parseSpanishDate(value);

  if (!isoDate) return "";

  const [year, month, day] = isoDate
    .split("-")
    .map(Number);

  const nextYear = year + 1;

  const lastDayOfMonth = new Date(
    Date.UTC(nextYear, month, 0)
  ).getUTCDate();

  const expiryDay = Math.min(
    day,
    lastDayOfMonth
  );

  return `${String(expiryDay).padStart(
    2,
    "0"
  )}/${String(month).padStart(
    2,
    "0"
  )}/${nextYear}`;
}
function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return (
      error.response?.data?.error ??
      "No se pudo completar la operación"
    );
  }

  return "No se pudo completar la operación";
}

function estadoComercialBadge(
  cliente: CrmCliente
) {
  if (cliente.estado === "archivado") {
    return <Badge variant="neutral">Archivado</Badge>;
  }

  if (cliente.estadoComercial === "cliente") {
    return <Badge variant="success">Cliente</Badge>;
  }

  if (cliente.estadoComercial === "inactivo") {
    return <Badge variant="warning">Inactivo</Badge>;
  }

  return <Badge variant="info">Prospect</Badge>;
}

export function CrmClientesTab() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageContracts =
    user?.role === "admin" || user?.role === "director_comercial";
  const [clientes, setClientes] = useState<CrmCliente[]>(
    []
  );
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState(() => searchParams.get("estado") ?? "");
  const [estadoComercial, setEstadoComercial] = useState(
    () => searchParams.get("estado_comercial") ?? ""
  );
  const [sinActividadDias, setSinActividadDias] = useState(
    () => searchParams.get("sin_actividad_dias") ?? ""
  );
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listError, setListError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] =
    useState<ClienteForm>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [prospectModalOpen, setProspectModalOpen] = useState(false);
  const [prospectForm, setProspectForm] =
    useState<ProspectForm>(EMPTY_PROSPECT_FORM);
  const [prospectError, setProspectError] = useState("");
  const [savingProspect, setSavingProspect] = useState(false);
  const [fichaEmpresaId, setFichaEmpresaId] = useState<number | null>(null);
  const [editingCliente, setEditingCliente] =
  useState<CrmCliente | null>(null);
  const loadClientes = useCallback(async () => {
    setLoading(true);
    setListError("");

    try {
      const response =
        await api.get<ClientesResponse>(
          "/admin/crm/clientes",
          {
            params: {
              page,
              limit: 20,
              search: search.trim() || undefined,
              estado: estado || undefined,
              estado_comercial: estadoComercial || undefined,
              sin_actividad_dias: sinActividadDias || undefined,
            },
          }
        );

      setClientes(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setClientes([]);
      setTotal(0);
      setTotalPages(1);
      setListError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [estado, estadoComercial, page, search, sinActividadDias]);
  const exportClientes = async () => {
    setExporting(true);
    setListError("");

    try {
      const allClientes: CrmCliente[] = [];
      let exportPage = 1;
      let exportTotalPages = 1;

      do {
        const response =
          await api.get<ClientesResponse>(
            "/admin/crm/clientes",
            {
              params: {
                page: exportPage,
                limit: 100,
              },
            }
          );

        allClientes.push(...response.data.data);
        exportTotalPages =
          response.data.totalPages;
        exportPage += 1;
      } while (exportPage <= exportTotalPages);

      const date = new Date()
        .toISOString()
        .slice(0, 10);

      await exportCrmExcel({
        filename: `clientes-crm-${date}.xlsx`,
        sheetName: "Clientes CRM",
        rows: allClientes,
        columns: [
          {
            header: "ID cliente",
            key: "id_cliente",
            width: 14,
            value: (row) => row.idExterno,
          },
          {
            header: "Razón social",
            key: "razon_social",
            width: 34,
            value: (row) => row.razonSocial,
          },
          {
            header: "Situación comercial",
            key: "estado_comercial",
            width: 20,
            value: (row) => row.estadoComercial,
          },
          {
            header: "Origen",
            key: "origen",
            width: 28,
            value: (row) => row.origen,
          },
          {
            header: "CIF / NIF",
            key: "cif",
            width: 18,
            value: (row) => row.cif,
          },
          {
            header: "Dirección",
            key: "direccion",
            width: 34,
            value: (row) => row.direccion,
          },
          {
            header: "Población",
            key: "poblacion",
            width: 24,
            value: (row) => row.poblacion,
          },
          {
            header: "Código postal",
            key: "codigo_postal",
            width: 16,
            value: (row) => row.codigoPostal,
          },
          {
            header: "País",
            key: "pais",
            width: 18,
            value: (row) => row.pais,
          },
          {
            header: "Persona de contacto",
            key: "persona_contacto",
            width: 28,
            value: (row) => row.personaContacto,
          },
          {
            header: "Email",
            key: "email",
            width: 34,
            value: (row) => row.emailContacto,
          },
          {
            header: "Teléfono",
            key: "telefono",
            width: 18,
            value: (row) => row.telefono,
          },
          {
            header: "Estado",
            key: "estado",
            width: 14,
            value: (row) => row.estado,
          },
        ],
      });
    } catch (error) {
      setListError(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => {
    const timer = window.setTimeout(
      loadClientes,
      250
    );

    return () => window.clearTimeout(timer);
  }, [loadClientes]);

  const openCreateModal = () => {
    setEditingCliente(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  };

  const openProspectModal = () => {
    setProspectForm(EMPTY_PROSPECT_FORM);
    setProspectError("");
    setProspectModalOpen(true);
  };

  const closeProspectModal = () => {
    if (savingProspect) return;
    setProspectModalOpen(false);
    setProspectError("");
  };

  const setProspectField = (
    field: keyof ProspectForm,
    value: string
  ) => {
    setProspectForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const createProspect = async () => {
    setProspectError("");

    if (!prospectForm.razonSocial.trim()) {
      setProspectError("Indica el nombre o la razón social de la empresa.");
      return;
    }

    setSavingProspect(true);

    try {
      const created = await api.post<{ id: number }>("/admin/crm/clientes/prospect", {
        razon_social: prospectForm.razonSocial,
        nombre_comercial: prospectForm.nombreComercial || null,
        cif: prospectForm.cif || null,
        persona_contacto: prospectForm.personaContacto || null,
        telefono: prospectForm.telefono || null,
        email_contacto: prospectForm.emailContacto || null,
        origen: prospectForm.origen || null,
      });

      setProspectModalOpen(false);
      setProspectForm(EMPTY_PROSPECT_FORM);
      if (page !== 1) setPage(1);
      else await loadClientes();
      setFichaEmpresaId(created.data.id);
    } catch (error) {
      setProspectError(getErrorMessage(error));
    } finally {
      setSavingProspect(false);
    }
  };
  const openEditModal = (
  cliente: CrmCliente
) => {
  setEditingCliente(cliente);
  setForm({
    ...EMPTY_FORM,
    razonSocial: cliente.razonSocial,
    cif: cliente.cif ?? "",
    direccion: cliente.direccion ?? "",
    poblacion: cliente.poblacion ?? "",
    codigoPostal:
      cliente.codigoPostal ?? "",
    pais: cliente.pais || "España",
    personaContacto:
      cliente.personaContacto ?? "",
    telefono: cliente.telefono ?? "",
    emailContacto:
      cliente.emailContacto ?? "",
  });
  setFormError("");
  setModalOpen(true);
};
useEffect(() => {
  if (
    searchParams.get("nueva") !== "1"
  ) {
    return;
  }
setEditingCliente(null);
  setForm(EMPTY_FORM);
  setFormError("");
  setModalOpen(true);

  const nextParams =
    new URLSearchParams(searchParams);

  nextParams.delete("nueva");

  setSearchParams(
    nextParams,
    { replace: true }
  );
}, [searchParams, setSearchParams]);
  const closeCreateModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingCliente(null);
    setFormError("");
  };

  const setField = (
    field: keyof ClienteForm,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const createCliente = async () => {
  setFormError("");
if (editingCliente) {
  const isProspect = editingCliente.estadoComercial === "prospect";
  const requiredValues = isProspect
    ? [form.razonSocial]
    : [
        form.razonSocial,
        form.cif,
        form.direccion,
        form.poblacion,
        form.codigoPostal,
        form.personaContacto,
        form.telefono,
        form.emailContacto,
      ];

  if (
    requiredValues.some(
      (value) => !value.trim()
    )
  ) {
    setFormError(
      "Completa todos los campos obligatorios."
    );
    return;
  }

  setSaving(true);

  try {
    await api.patch(
      `/admin/crm/clientes/${editingCliente.id}${isProspect ? "/comercial" : ""}`,
      {
        razon_social: form.razonSocial,
        cif: form.cif || null,
        direccion: form.direccion || null,
        poblacion: form.poblacion || null,
        codigo_postal: form.codigoPostal || null,
        pais: form.pais,
        persona_contacto: form.personaContacto || null,
        telefono: form.telefono || null,
        email_contacto: form.emailContacto || null,
      }
    );

    setModalOpen(false);
    setEditingCliente(null);
    setForm(EMPTY_FORM);
    await loadClientes();
  } catch (error) {
    setFormError(
      getErrorMessage(error)
    );
  } finally {
    setSaving(false);
  }

  return;
}
    const requiredValues = [
    form.razonSocial,
    form.cif,
    form.direccion,
    form.poblacion,
    form.codigoPostal,
    form.personaContacto,
    form.telefono,
    form.emailContacto,
    form.numeroPoliza,
    form.aseguradora,
    form.fechaEfecto,
    form.fechaVencimiento,
  ];

  const importesIncompletos =
    form.tipoPoliza !== "ACC" &&
    [
      form.primaNeta,
      form.impuestos,
      form.recargo,
      form.consorcio,
      form.primaTotal,
      form.comisionPorcentaje,
    ].some(
      (value) => !value.trim()
    );

  if (
    requiredValues.some(
      (value) => !value.trim()
    ) ||
    importesIncompletos
  ) {
    setFormError(
      "Completa todos los campos obligatorios."
    );
    return;
  }

  const fechaEfecto =
    parseSpanishDate(form.fechaEfecto);

  const fechaVencimiento =
    parseSpanishDate(form.fechaVencimiento);

  if (!fechaEfecto || !fechaVencimiento) {
    setFormError(
      "Introduce las fechas con el formato DD/MM/AAAA."
    );
    return;
  }

  if (fechaVencimiento <= fechaEfecto) {
    setFormError(
      "La fecha de vencimiento debe ser posterior a la fecha de efecto."
    );
    return;
  }

  if (
    form.tipoPoliza === "ACC" &&
    form.passwordAcceso.length < 8
  ) {
    setFormError(
      "La contraseña de acceso debe tener al menos 8 caracteres."
    );
    return;
  }

  setSaving(true);

  try {
    await api.post(
      "/admin/crm/clientes/contratacion",
      {
        razon_social: form.razonSocial,
        cif: form.cif,
        direccion: form.direccion,
        poblacion: form.poblacion,
        codigo_postal: form.codigoPostal,
        pais: form.pais,
        persona_contacto:
          form.personaContacto,
        telefono: form.telefono,
        email_contacto:
          form.emailContacto,

        tipo_poliza: form.tipoPoliza,
        numero_poliza:
          form.numeroPoliza,
        aseguradora: form.aseguradora,
        fecha_efecto: fechaEfecto,
        fecha_vencimiento:
          fechaVencimiento,

                prima_neta:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.primaNeta,
        impuestos:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.impuestos,
        recargo:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.recargo,
        consorcio:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.consorcio,
        prima_total:
          form.tipoPoliza === "ACC"
            ? "0"
            : form.primaTotal,
        comision_porcentaje:
          form.tipoPoliza === "ACC"
            ? "20"
            : form.comisionPorcentaje,
          email_acceso:
          form.emailAcceso,
        password_acceso:
          form.passwordAcceso,
        metodo_pago: form.metodoPago,
      }
    );

    setModalOpen(false);
    setForm(EMPTY_FORM);

    if (page !== 1) {
      setPage(1);
    } else {
      await loadClientes();
    }
  } catch (error) {
    setFormError(
      getErrorMessage(error)
    );
  } finally {
    setSaving(false);
  }
};

  const columns: TableColumn<CrmCliente>[] = [
    {
      key: "idExterno",
      header: "ID cliente",
      render: (cliente) => (
        <span className="font-semibold text-[#1A3AB8]">
          {cliente.idExterno ?? "—"}
        </span>
      ),
    },
    {
      key: "razonSocial",
      header: "Empresa",
      render: (cliente) => (
        <div>
          <div className="font-medium">
            {cliente.nombreComercial || cliente.razonSocial}
          </div>
          {cliente.nombreComercial && (
            <div className="text-xs text-gray-500">
              {cliente.razonSocial}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "cif",
      header: "CIF / NIF",
      render: (cliente) => cliente.cif || "—",
    },
    {
      key: "personaContacto",
      header: "Persona de contacto",
      render: (cliente) =>
        cliente.personaContacto || "—",
    },
    {
      key: "emailContacto",
      header: "Email",
      render: (cliente) =>
        cliente.emailContacto || "—",
    },
    {
      key: "telefono",
      header: "Teléfono",
      render: (cliente) =>
        cliente.telefono || "—",
    },
    {
      key: "estadoComercial",
      header: "Situación",
      render: (cliente) =>
        estadoComercialBadge(cliente),
    },
  {
  key: "acciones",
  header: "Acciones",
  render: (cliente) => (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        onClick={() => setFichaEmpresaId(cliente.id)}
      >
        Abrir ficha
      </Button>
      {(canManageContracts || cliente.estadoComercial === "prospect") && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => openEditModal(cliente)}
        >
          Editar
        </Button>
      )}
    </div>
  ),
},
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Empresas CRM
          </h2>

          <p className="mt-1 text-sm text-gray-600">
            Prospects y clientes comparten una única ficha y
            evolucionan sin duplicar sus datos.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
  <Button
    variant="secondary"
    onClick={exportClientes}
    loading={exporting}
  >
    <Download className="h-4 w-4" />
    Exportar Excel
  </Button>

  <Button variant="secondary" onClick={openProspectModal}>
    <Plus className="h-4 w-4" />
    Nuevo prospect
  </Button>

  {canManageContracts && (
    <Button onClick={openCreateModal}>
      <Plus className="h-4 w-4" />
      Nueva contratación
    </Button>
  )}
</div>
      </div>

      <div className="grid gap-3 rounded-lg bg-gray-50 p-4 lg:grid-cols-[1fr_200px_210px_210px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

          <Input
            aria-label="Buscar empresas"
            placeholder="Buscar por ID, empresa, CIF o email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <select
          aria-label="Filtrar por estado"
          value={estado}
          onChange={(event) => {
            setEstado(event.target.value);
            setPage(1);
          }}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
          <option value="archivado">Archivados</option>
        </select>

        <select
          aria-label="Filtrar por situación comercial"
          value={estadoComercial}
          onChange={(event) => {
            setEstadoComercial(event.target.value);
            setPage(1);
          }}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="">Todos: prospects y clientes</option>
          <option value="prospect">Prospects</option>
          <option value="cliente">Clientes</option>
          <option value="inactivo">Inactivos</option>
        </select>

        <select
          aria-label="Filtrar por inactividad"
          value={sinActividadDias}
          onChange={(event) => {
            setSinActividadDias(event.target.value);
            setPage(1);
          }}
          className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
        >
          <option value="">Cualquier seguimiento</option>
          <option value="15">Sin actividad: 15 días</option>
          <option value="30">Sin actividad: 30 días</option>
          <option value="60">Sin actividad: 60 días</option>
          <option value="90">Sin actividad: 90 días</option>
        </select>
      </div>

      {listError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {listError}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {total === 1
            ? "1 empresa"
            : `${total} empresas`}
        </span>
      </div>

      <Table
        columns={columns}
        data={clientes}
        loading={loading}
        emptyMessage="Todavía no hay empresas en la cartera CRM"
        floatingHorizontalScrollbar
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <Modal
        isOpen={modalOpen}
        onClose={closeCreateModal}
        title={
  editingCliente
    ? editingCliente.estadoComercial === "prospect"
      ? "Editar prospect"
      : "Editar cliente"
    : "Nueva contratación"
}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeCreateModal}
              disabled={saving}
            >
              Cancelar
            </Button>

            <Button
              onClick={createCliente}
              loading={saving}
            >
              {editingCliente
  ? "Guardar cambios"
  : "Crear contratación"}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {!editingCliente && (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
    El ID de cliente no se escribe manualmente. La
    plataforma generará automáticamente el siguiente
    número disponible.
  </div>
)}

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {formError}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Razón social *"
              value={form.razonSocial}
              onChange={(event) =>
                setField(
                  "razonSocial",
                  event.target.value
                )
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "CIF o NIF" : "CIF o NIF *"}
              value={form.cif}
              onChange={(event) =>
                setField("cif", event.target.value)
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "Dirección completa" : "Dirección completa *"}
              value={form.direccion}
              onChange={(event) =>
                setField(
                  "direccion",
                  event.target.value
                )
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "Población" : "Población *"}
              value={form.poblacion}
              onChange={(event) =>
                setField(
                  "poblacion",
                  event.target.value
                )
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "Código postal" : "Código postal *"}
              value={form.codigoPostal}
              onChange={(event) =>
                setField(
                  "codigoPostal",
                  event.target.value
                )
              }
            />

            <Input
              label="País"
              value={form.pais}
              onChange={(event) =>
                setField("pais", event.target.value)
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "Persona de contacto" : "Persona de contacto *"}
              value={form.personaContacto}
              onChange={(event) =>
                setField(
                  "personaContacto",
                  event.target.value
                )
              }
            />

            <Input
              label={editingCliente?.estadoComercial === "prospect" ? "Teléfono" : "Teléfono *"}
              type="tel"
              value={form.telefono}
              onChange={(event) =>
                setField(
                  "telefono",
                  event.target.value
                )
              }
            />

            <div className="md:col-span-2">
              <Input
                label={editingCliente?.estadoComercial === "prospect" ? "Email de contacto" : "Email de contacto *"}
                type="email"
                value={form.emailContacto}
                onChange={(event) =>
                  setField(
                    "emailContacto",
                    event.target.value
                  )
                }
              />
            </div>
          </div>
        </div>
                {!editingCliente && (
  <div className="mt-5 space-y-5 border-t border-gray-200 pt-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Datos de la primera póliza
            </h3>

            <p className="mt-1 text-sm text-gray-600">
              El ID de póliza se generará automáticamente.
              Introduce solamente los datos reales de la
              aseguradora.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Tipo de póliza *
              </span>

              <select
                value={form.tipoPoliza}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    tipoPoliza:
                      event.target.value as TipoPoliza,
                  }))
                }
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
              >
                {TIPOS_POLIZA.map((tipo) => (
                  <option
                    key={tipo.value}
                    value={tipo.value}
                  >
                    {tipo.label}
                  </option>
                ))}
              </select>
            </label>

            <Input
              label="N.º de póliza *"
              value={form.numeroPoliza}
              onChange={(event) =>
                setField(
                  "numeroPoliza",
                  event.target.value
                )
              }
            />

            <div className="md:col-span-2">
              <Input
                label="Aseguradora *"
                value={form.aseguradora}
                onChange={(event) =>
                  setField(
                    "aseguradora",
                    event.target.value
                  )
                }
              />
            </div>

            <Input
              label="Fecha de efecto *"
              placeholder="DD/MM/AAAA"
              value={form.fechaEfecto}
              onChange={(event) => {
                const fecha = formatDateInput(
                  event.target.value
                );

                setForm((current) => ({
                  ...current,
                  fechaEfecto: fecha,
                  fechaVencimiento:
                    calculateAnnualExpiry(fecha),
                }));
              }}
            />

            <Input
              label="Fecha de vencimiento *"
              placeholder="DD/MM/AAAA"
              value={form.fechaVencimiento}
              onChange={(event) =>
                setField(
                  "fechaVencimiento",
                  formatDateInput(
                    event.target.value
                  )
                )
              }
            />
          {form.tipoPoliza === "ACC" ? (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 md:col-span-2">
    <p className="font-semibold text-blue-900">
      Importes automáticos
    </p>
    <p className="mt-1 text-sm text-blue-800">
      Los importes y la comisión se calcularán
      automáticamente con los pilotos pagados
      durante la vigencia de esta póliza.
    </p>
  </div>
) : (
  <>
  <Input
  label="Prima neta (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.primaNeta}
  onChange={(event) =>
    setField(
      "primaNeta",
      event.target.value
    )
  }
/>

<Input
  label="Impuestos (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.impuestos}
  onChange={(event) =>
    setField(
      "impuestos",
      event.target.value
    )
  }
/>

<Input
  label="Recargo (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.recargo}
  onChange={(event) =>
    setField(
      "recargo",
      event.target.value
    )
  }
/>

<Input
  label="Consorcio (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.consorcio}
  onChange={(event) =>
    setField(
      "consorcio",
      event.target.value
    )
  }
/>

<Input
  label="Prima total (€) *"
  type="number"
  min="0"
  step="0.01"
  value={form.primaTotal}
  onChange={(event) =>
    setField(
      "primaTotal",
      event.target.value
    )
  }
/>

<Input
  label="Comisión sobre prima neta (%) *"
  type="number"
  min="0"
  max="100"
  step="0.01"
  value={form.comisionPorcentaje}
  onChange={(event) =>
    setField(
      "comisionPorcentaje",
      event.target.value
    )
  }
/>  </>
)}
          </div>
        </div>
        )}

                {!editingCliente &&
  form.tipoPoliza === "ACC" && (
          <div className="mt-5 space-y-5 border-t border-gray-200 pt-5">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h3 className="font-semibold text-blue-900">
                Acceso a la plataforma
              </h3>

              <p className="mt-1 text-sm text-blue-800">
                Al crear esta contratación, el cliente también
                aparecerá automáticamente en la sección
                Clientes.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Email de acceso"
                type="email"
                placeholder="Vacío = email de contacto"
                value={form.emailAcceso}
                onChange={(event) =>
                  setField(
                    "emailAcceso",
                    event.target.value
                  )
                }
              />

              <Input
                label="Contraseña de acceso *"
                type="password"
                value={form.passwordAcceso}
                onChange={(event) =>
                  setField(
                    "passwordAcceso",
                    event.target.value
                  )
                }
              />

              <label className="block md:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Método de pago *
                </span>

                <select
                  value={form.metodoPago}
                  onChange={(event) =>
                    setField(
                      "metodoPago",
                      event.target.value
                    )
                  }
                  className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
                >
                  <option value="tpv">TPV</option>
                  <option value="transferencia">
                    Transferencia
                  </option>
                </select>
              </label>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={prospectModalOpen}
        onClose={closeProspectModal}
        title="Nuevo prospect"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeProspectModal}
              disabled={savingProspect}
            >
              Cancelar
            </Button>
            <Button onClick={createProspect} loading={savingProspect}>
              Crear prospect
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            Solo el nombre es obligatorio. Podrás completar la ficha y añadir
            oportunidades o pólizas más adelante, sin crear otra empresa.
          </div>

          {prospectError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {prospectError}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Nombre o razón social *"
              value={prospectForm.razonSocial}
              onChange={(event) =>
                setProspectField("razonSocial", event.target.value)
              }
            />

            <Input
              label="Nombre comercial"
              value={prospectForm.nombreComercial}
              onChange={(event) =>
                setProspectField("nombreComercial", event.target.value)
              }
            />

            <Input
              label="CIF o NIF"
              value={prospectForm.cif}
              onChange={(event) =>
                setProspectField("cif", event.target.value)
              }
            />

            <CrmOrigenSelect
              value={prospectForm.origen}
              onChange={(value) =>
                setProspectField("origen", value)
              }
            />

            <Input
              label="Persona de contacto"
              value={prospectForm.personaContacto}
              onChange={(event) =>
                setProspectField("personaContacto", event.target.value)
              }
            />

            <Input
              label="Teléfono"
              type="tel"
              value={prospectForm.telefono}
              onChange={(event) =>
                setProspectField("telefono", event.target.value)
              }
            />

            <div className="md:col-span-2">
              <Input
                label="Email"
                type="email"
                value={prospectForm.emailContacto}
                onChange={(event) =>
                  setProspectField("emailContacto", event.target.value)
                }
              />
            </div>
          </div>
        </div>
      </Modal>

      <CrmEmpresaFichaModal
        empresaId={fichaEmpresaId}
        onClose={() => setFichaEmpresaId(null)}
        onUpdated={() => {
          void loadClientes();
        }}
        onDeleted={() => {
          setFichaEmpresaId(null);
          void loadClientes();
        }}
      />
    </div>
  );
}
